import { useState, useEffect } from 'react';
import { getStaffList, updateStaffMember, addStaffMember, deleteStaffMember, type StaffMember } from '../utils/api';
import { useTelegram } from '../hooks/useTelegram';

export function StaffPage(): React.JSX.Element {
  const { tg, initData } = useTelegram();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    fetchStaff();
  }, [initData]);

  const fetchStaff = async () => {
    if (!initData) return;
    const res = await getStaffList(initData);
    if (res.ok) {
      setStaff(res.value.staff);
    }
    setLoading(false);
  };

  const togglePermission = async (staffId: string, perm: string) => {
    const member = staff.find(s => s.id === staffId);
    if (!member) return;

    const newValue = !member.permissions[perm];
    setStaff(prev => prev.map(s => s.id === staffId 
      ? { ...s, permissions: { ...s.permissions, [perm]: newValue } } 
      : s
    ));

    await updateStaffMember(initData, staffId, { 
      permissions: { ...member.permissions, [perm]: newValue } 
    });
  };

  const handleAddStaff = async () => {
    if (!newName.trim()) {
      tg?.showAlert('Введите имя сотрудника');
      return;
    }
    setLoading(true);
    const res = await addStaffMember(initData, newName, 'master');
    if (res.ok) {
      setStaff(prev => [...prev, res.value.member]);
      setIsAdding(false);
      setNewName('');
      tg?.HapticFeedback.notificationOccurred('success');
    } else {
      tg?.showAlert('Ошибка при добавлении сотрудника');
    }
    setLoading(false);
  };

  const handleDeleteStaff = async (id: string, name: string) => {
    tg?.showConfirm(`Вы уверены, что хотите удалить сотрудника ${name}?`, async (ok) => {
      if (ok) {
        setLoading(true);
        const res = await deleteStaffMember(initData, id);
        if (res.ok) {
          setStaff(prev => prev.filter(s => s.id !== id));
          tg?.HapticFeedback.notificationOccurred('warning');
        } else {
          tg?.showAlert('Ошибка при удалении');
        }
        setLoading(false);
      }
    });
  };

  const handleRename = async (id: string, currentName: string) => {
    (tg as any)?.showPopup({
      title: 'Переименовать',
      message: `Введите новое имя для ${currentName}`,
      buttons: [{ id: 'ok', type: 'default', text: 'Сохранить' }, { type: 'cancel' }]
    }, async (buttonId: string) => {
      // К сожалению, Telegram Popup не возвращает введенный текст.
      // Используем обычный prompt для простоты в демо или кастомный инпут.
      const name = prompt('Новое имя:', currentName);
      if (name && name !== currentName) {
        const res = await updateStaffMember(initData, id, { name });
        if (res.ok) {
          setStaff(prev => prev.map(s => s.id === id ? { ...s, name } : s));
        }
      }
    });
  };

  if (loading && staff.length === 0) {
    return <div className="p-10 text-center">Загрузка команды...</div>;
  }

  return (
    <div className="p-1 animate-in fade-in">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">👥 Команда</h1>
        <button 
          onClick={() => setIsAdding(true)} 
          className="bg-tg-theme-button text-tg-theme-button-text px-4 py-2 rounded-xl text-sm font-bold shadow-lg"
        >
          + Пригласить
        </button>
      </div>

      {isAdding && (
        <div className="bg-tg-theme-bg p-5 rounded-2xl border border-black/10 mb-6 shadow-xl animate-in slide-in-from-top">
          <h3 className="font-bold mb-3">Новый сотрудник</h3>
          <input 
            autoFocus
            className="w-full bg-black/5 dark:bg-white/5 border-none rounded-xl px-4 py-3 outline-none focus:ring-2 ring-blue-500 mb-4"
            placeholder="Имя и фамилия"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <div className="flex gap-2">
            <button onClick={handleAddStaff} className="flex-1 bg-tg-theme-button text-tg-theme-button-text py-3 rounded-xl font-bold">Добавить</button>
            <button onClick={() => setIsAdding(false)} className="flex-1 bg-black/5 py-3 rounded-xl font-bold">Отмена</button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {staff.length === 0 && !isAdding && (
          <div className="text-center py-20 opacity-50">
            <div className="text-4xl mb-2">🤷‍♂️</div>
            <p>У вас пока нет сотрудников</p>
          </div>
        )}
        
        {staff.map(member => (
          <div key={member.id} className="bg-tg-theme-bg p-5 rounded-3xl shadow-sm border border-black/5 relative overflow-hidden group">
            <div className="flex justify-between items-start">
              <div onClick={() => handleRename(member.id, member.name)} className="cursor-pointer">
                <div className="font-bold text-lg flex items-center gap-2">
                  {member.name}
                  <span className="text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">✏️</span>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-tg-theme-hint">{member.role}</div>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => setEditingId(editingId === member.id ? null : member.id)}
                  className={`p-2 rounded-full transition-colors ${editingId === member.id ? 'bg-blue-500 text-white' : 'bg-black/5'}`}
                >
                  ⚙️
                </button>
                <button 
                  onClick={() => handleDeleteStaff(member.id, member.name)}
                  className="p-2 rounded-full bg-red-500/10 text-red-500"
                >
                  🗑️
                </button>
              </div>
            </div>

            {editingId === member.id && (
              <div className="mt-4 pt-4 border-t border-black/5 animate-in slide-in-from-top">
                <p className="text-[10px] font-bold text-tg-theme-hint uppercase mb-3">Настройка прав доступа</p>
                <PermissionToggle 
                  label="Видеть все записи" 
                  active={member.permissions?.viewAllBookings || false} 
                  onToggle={() => togglePermission(member.id, 'viewAllBookings')} 
                />
                <PermissionToggle 
                  label="Управление расписанием" 
                  active={member.permissions?.manageSchedule || false} 
                  onToggle={() => togglePermission(member.id, 'manageSchedule')} 
                />
                <PermissionToggle 
                  label="Управление персоналом" 
                  active={member.permissions?.manageStaff || false} 
                  onToggle={() => togglePermission(member.id, 'manageStaff')} 
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PermissionToggle({ label, active, onToggle }: { label: string, active: boolean, onToggle: () => void }) {
  return (
    <div className="flex justify-between items-center py-2">
      <span className="text-sm">{label}</span>
      <div 
        onClick={onToggle}
        className={`w-10 h-5 rounded-full relative transition-colors cursor-pointer ${active ? 'bg-blue-500' : 'bg-black/20'}`}
      >
        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${active ? 'left-5.5' : 'left-0.5'}`} />
      </div>
    </div>
  );
}
